import {
  Dialog,
  Slot,
  Tooltip as RadixTooltip,
  VisuallyHidden as RadixVisuallyHidden,
} from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx, type ClassValue } from "clsx";
import {
  createElement,
  forwardRef,
  useRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type Ref,
  type ReactNode,
} from "react";
import { twMerge } from "tailwind-merge";

export { Popover, RadioGroup } from "radix-ui";
export {
  ArrowRight,
  ArrowRightLeft,
  Check,
  ChevronDown,
  CircleCheck,
  CircleUserRound,
  CircleX,
  Copy,
  Eye,
  Flame,
  Globe2,
  HandCoins,
  LoaderCircle,
  LogOut,
  RotateCcw,
  Tag,
  WalletCards,
} from "lucide-react";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const buttonVariants = cva(
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary: "border border-border bg-card text-foreground hover:bg-muted",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        ghost: "text-foreground hover:bg-muted",
      },
      size: {
        default: "min-h-11 px-4",
        compact: "min-h-9 px-3 text-xs",
        icon: "size-11 p-0",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

export interface ButtonProps
  extends ComponentPropsWithoutRef<"button">, VariantProps<typeof buttonVariants> {
  readonly asChild?: boolean;
}

export const Button = forwardRef<ElementRef<"button">, ButtonProps>(function Button(
  { asChild = false, className, size, variant, type = "button", ...props },
  ref,
) {
  const mergedClassName = cn(buttonVariants({ className, size, variant }));
  if (asChild) {
    return createElement(Slot.Root, {
      ref: ref as Ref<HTMLElement>,
      className: mergedClassName,
      ...{ "data-ui": "button" },
      ...props,
    });
  }
  return createElement("button", {
    ref,
    className: mergedClassName,
    type,
    "data-ui": "button",
    ...props,
  });
});

export interface ActionTooltipProps {
  readonly label?: ReactNode;
  readonly children: ReactNode;
  readonly disabled?: boolean;
  readonly side?: "top" | "right" | "bottom" | "left";
  readonly align?: "start" | "center" | "end";
  readonly triggerClassName?: string;
  readonly className?: string;
}

/**
 * Accessible action help with an immediate, styled hover/focus treatment.
 * Disabled controls use a focusable wrapper because native disabled buttons
 * cannot dispatch pointer or focus events themselves.
 */
export function ActionTooltip({
  label,
  children,
  disabled = false,
  side = "top",
  align = "center",
  triggerClassName,
  className,
}: ActionTooltipProps) {
  if (label === undefined || label === null || label === "") return children;
  const trigger = disabled
    ? createElement(
        RadixTooltip.Trigger,
        { asChild: true },
        createElement(
          "span",
          {
            className: cn("inline-flex", triggerClassName),
            tabIndex: 0,
            "aria-disabled": true,
            "data-ui": "tooltip-disabled-trigger",
          },
          children,
        ),
      )
    : createElement(RadixTooltip.Trigger, { asChild: true }, children);
  return createElement(RadixTooltip.Provider, {
    delayDuration: 0,
    skipDelayDuration: 0,
    disableHoverableContent: true,
    children: createElement(
      RadixTooltip.Root,
      { delayDuration: 0 },
      trigger,
      createElement(
        RadixTooltip.Portal,
        null,
        createElement(
          RadixTooltip.Content,
          {
            side,
            align,
            sideOffset: 8,
            collisionPadding: 12,
            className: cn("to-action-tooltip", className),
          },
          label,
        ),
      ),
    ),
  });
}

export interface ButtonGroupProps extends HTMLAttributes<HTMLDivElement> {
  readonly orientation?: "horizontal" | "vertical";
}

/** Keep spacing on the container so wrapped and disabled actions remain separated. */
export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(function ButtonGroup(
  { className, orientation = "horizontal", ...props },
  ref,
) {
  return createElement("div", {
    ref,
    role: "group",
    "data-ui": "button-group",
    "data-orientation": orientation,
    className: cn(
      "flex min-w-0 flex-wrap gap-3",
      orientation === "vertical" ? "flex-col items-stretch" : "items-center",
      className,
    ),
    ...props,
  });
});

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function Card(
  { className, ...props },
  ref,
) {
  return createElement("div", {
    ref,
    className: cn(
      "rounded-lg border border-border bg-card text-card-foreground shadow-sm",
      className,
    ),
    ...props,
  });
});

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return createElement("div", {
      ref,
      className: cn("flex flex-col gap-1.5 p-6", className),
      ...props,
    });
  },
);

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return createElement("div", {
      ref,
      className: cn("p-6 pt-0", className),
      ...props,
    });
  },
);

const statusBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
  {
    variants: {
      status: {
        fundraising:
          "border-status-fundraising/40 bg-status-fundraising/10 text-status-fundraising",
        pendingLp: "border-status-pending-lp/40 bg-status-pending-lp/10 text-status-pending-lp",
        active: "border-status-active/40 bg-status-active/10 text-status-active",
        failed: "border-status-failed/40 bg-status-failed/10 text-status-failed",
        loading: "border-data-loading/40 bg-data-loading/10 text-data-loading",
        fresh: "border-data-fresh/40 bg-data-fresh/10 text-data-fresh",
        refreshing: "border-data-refreshing/40 bg-data-refreshing/10 text-data-refreshing",
        stale: "border-data-stale/40 bg-data-stale/10 text-data-stale",
        restoring: "border-data-restoring/40 bg-data-restoring/10 text-data-restoring",
        unavailable: "border-data-unavailable/40 bg-data-unavailable/10 text-data-unavailable",
        protocolLocked:
          "border-risk-protocol-locked/40 bg-risk-protocol-locked/10 text-risk-protocol-locked",
        creatorRisk:
          "border-risk-creator-managed/40 bg-risk-creator-managed/10 text-risk-creator-managed",
      },
    },
  },
);

export interface StatusBadgeProps
  extends
    Omit<HTMLAttributes<HTMLSpanElement>, "children">,
    VariantProps<typeof statusBadgeVariants> {
  readonly label: ReactNode;
  readonly icon?: ReactNode;
}

export function StatusBadge({ className, icon, label, status, ...props }: StatusBadgeProps) {
  return createElement(
    "span",
    { className: cn(statusBadgeVariants({ className, status })), ...props },
    icon === undefined ? null : createElement("span", { "aria-hidden": true }, icon),
    createElement("span", null, label),
  );
}

export const VisuallyHidden = RadixVisuallyHidden.Root;

export function ResponsiveDialog({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  children,
  footer,
  size = "default",
  dismissible = true,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string;
  readonly closeLabel: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly size?: "default" | "wide" | "workbench";
  readonly dismissible?: boolean;
}) {
  const returnFocus = useRef<HTMLElement | null>(null);
  return createElement(
    Dialog.Root,
    {
      open,
      onOpenChange: (next: boolean) => {
        if (next || dismissible) onOpenChange(next);
      },
    },
    createElement(
      Dialog.Portal,
      null,
      createElement(Dialog.Overlay, {
        className: "fixed inset-0 z-40 bg-background/80 backdrop-blur-sm",
      }),
      createElement(
        Dialog.Content,
        {
          className: cn(
            "fixed inset-x-0 bottom-0 z-50 flex max-h-[100dvh] flex-col rounded-t-xl border bg-card text-card-foreground shadow-xl sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90dvh] sm:w-[min(34rem,calc(100vw-2rem))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl",
            size === "wide" && "sm:w-[min(52rem,calc(100vw-2rem))]",
            size === "workbench" &&
              "h-[100dvh] rounded-none sm:h-[90dvh] sm:w-[min(68rem,calc(100vw-2rem))]",
          ),
          onEscapeKeyDown: (event: KeyboardEvent) => {
            if (!dismissible) event.preventDefault();
          },
          onInteractOutside: (event: Event) => {
            if (!dismissible || size === "workbench") event.preventDefault();
          },
          "aria-describedby": description === undefined ? undefined : "product-dialog-description",
          onOpenAutoFocus: () => {
            returnFocus.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          },
          onCloseAutoFocus: (event: Event) => {
            event.preventDefault();
            if (returnFocus.current?.isConnected && !returnFocus.current.hasAttribute("disabled"))
              returnFocus.current.focus();
          },
        },
        createElement(
          "div",
          { className: "flex shrink-0 items-center justify-between gap-3 border-b p-4" },
          createElement(Dialog.Title, { className: "text-lg font-bold" }, title),
          createElement(
            Dialog.Close,
            {
              className: buttonVariants({ variant: "ghost", size: "icon" }),
              "aria-label": closeLabel,
              disabled: !dismissible,
            },
            "×",
          ),
        ),
        description === undefined
          ? null
          : createElement(
              Dialog.Description,
              {
                id: "product-dialog-description",
                className: "px-5 pt-4 text-sm text-muted-foreground",
              },
              description,
            ),
        createElement(
          "div",
          {
            className:
              "min-h-0 overflow-y-auto overscroll-contain p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]",
          },
          children,
        ),
        footer === undefined
          ? null
          : createElement(
              "div",
              {
                className:
                  "product-dialog-footer shrink-0 border-t bg-card p-4 pb-[max(1rem,env(safe-area-inset-bottom))]",
                "data-testid": "dialog-footer",
              },
              footer,
            ),
      ),
    ),
  );
}
